package org.multiplatform.kotfolio.pages

import androidx.compose.runtime.*
import org.multiplatform.kotfolio.components.KotlinCode
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText

import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul

@Page("android_communicate_fragment")
@Composable
fun RoomDatabaseAndroidPage() {
    Column(
        modifier = Modifier.fillMaxSize().padding(leftRight = 0.1.em),
        horizontalAlignment = Alignment.Start
    ) {
        SpanText(
            text = "All Fragment to Fragment communication has to be done either through a shared ViewModel or through the associated Activity. Two Fragments should never communicate directly.",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
        )

        SpanText(
            text = "Below are some of the most preferred options to communicate between two fragments or communicate between fragment and host activity. The recommended option depends on the use case.",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "View Model",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "To share persistent data with custom APIs, use a ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Both fragments can access the ViewModel through their containing Activity.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The Fragments can update data within the ViewModel and if the data is exposed using LiveData the new state will be pushed to the other fragment as long as it is observing the LiveData from the ViewModel.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "Fragment Result API",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "For a one-time result with data that can be placed in a Bundle, use the Fragment Result API.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "Define an Interface",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Ul {
                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Define an interface in your Fragment A, and let your Activity implement that Interface.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Now you can call the interface method in your first Fragment, and your Activity will receive the event.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Now in your activity, you can call your second Fragment to update the textview with the received value.",
                        modifier = Modifier
                            .fontSize(FontSize.Medium)
                    )
                }
            }
        }

        SpanText(
            text = "Communicate using ViewModel",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "ViewModel is an ideal choice when you need to share data between multiple fragments or between fragments and their host activity.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }
        }

        SpanText(
            text = "Share data between fragments & its host activity",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "In some cases, you might need to share data between fragments and their host activity.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "In this case both your fragment and its host activity can retrieve a shared instance of a ViewModel with activity scope by passing the activity into the ViewModelProvider constructor.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "The ViewModelProvider handles instantiating the ViewModel or retrieving it if it already exists. Both components can observe and modify this data.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }
        }

        KotlinCode(
            code = """
                    // Create View model
                    class YourViewModel : ViewModel() {
                        private val mutableSelectedItem = MutableLiveData<Item>()

                        val selectedItem: LiveData<Item> get() = mutableSelectedItem

                        fun selectItem(item: Item) {
                            mutableSelectedItem.value = item
                        }
                    }

                    // Your Host activity
                    class YourActivity : AppCompatActivity() {

                        private val viewModel: YourViewModel by viewModels()

                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            viewModel.selectedItem.observe(this, Observer { item ->
                                // Perform an action
                            })
                        }
                    }

                    // Your Fragment
                    class YourFragment : Fragment() {

                        // viewmodel is in host activity scope
                        private val viewModel: YourViewModel by activityViewModels()

                        // Called when the item is clicked.
                        fun onItemClicked(item: Item) {
                            // Set a new item
                            viewModel.selectItem(item)
                        }
                    }
                    """.trimIndent()
        )

        SpanText(
            text = "Share data between fragments",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "Two or more fragments in the same activity can share a ViewModel using their activity scope to communicate with each other.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "By sharing the ViewModel in this way, the fragments don't need to know about each other, and the activity doesn't need to do anything to facilitate the communication.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "In the below example both the fragments use their host activity as the scope for the ViewModelProvider.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "Because the fragments use the same scope, they receive the same instance of the ViewModel, which enables them to communicate back and forth.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }
        }

        KotlinCode(
            code = """
                    // Your View Model
                    class YourViewModel : ViewModel() {
                        val filters = MutableLiveData<Set<Filter>>()

                        private val originalList: LiveData<List<Item>>() = ...
                        val filteredList: LiveData<List<Item>> = ...

                        fun addFilter(filter: Filter) { ... }

                        fun removeFilter(filter: Filter) { ... }
                    }

                    // Fragment 1
                    class ListFragment : Fragment() {
                        
                        // ViewModel is in activity scope.
                        private val viewModel: YourViewModel by activityViewModels()

                        override fun onViewCreated(view: View, savedInstanceState: Bundle?) {
                            viewModel.filteredList.observe(viewLifecycleOwner, Observer { list ->
                                // Perform action
                            }
                        }
                    }

                    // Fragment 2
                    class FilterFragment : Fragment() {

                        // ViewModel is in activity scope.
                        private val viewModel: YourViewModel by activityViewModels()
                        override fun onViewCreated(view: View, savedInstanceState: Bundle?) {

                            viewModel.filters.observe(viewLifecycleOwner, Observer { set ->
                                // Update the selected filters UI.
                            }
                        }

                        fun onFilterSelected(filter: Filter) = viewModel.addFilter(filter)

                        fun onFilterDeselected(filter: Filter) = viewModel.removeFilter(filter)
                    }
                    """.trimIndent()
        )

        SpanText(
            text = "Share data between a parent and child fragment",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "When working with child fragments, we have to use the parent fragment as the ViewModel scope, as shown in the following example:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }
        }

        KotlinCode(
            code = """
                    // Your View Model
                    class YourViewModel : ViewModel() {
                        val filters = MutableLiveData<Set<Filter>>()

                        private val originalList: LiveData<List<Item>>() = ...
                        val filteredList: LiveData<List<Item>> = ...

                        fun addFilter(filter: Filter) { ... }

                        fun removeFilter(filter: Filter) { ... }
                    }

                    // Your Parent Fragment
                    class ListFragment: Fragment() {
                        private val viewModel: ListViewModel by viewModels()
                        override fun onViewCreated(view: View, savedInstanceState: Bundle?) {
                            viewModel.filteredList.observe(viewLifecycleOwner, Observer { list ->
                                // Update the list UI.
                            }
                        }
                    }

                    // Your Child Fragment
                    class ChildFragment: Fragment() {
                        // ViewModel is using parent fragment's scope
                        private val viewModel: ListViewModel by viewModels({requireParentFragment()})
                        ...
                    }
                    """.trimIndent()
        )

        SpanText(
            text = "Scope a ViewModel to the Navigation Graph",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "If you’re using the Navigation library, you can also scope a ViewModel to the lifecycle of a destination's NavBackStackEntry.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "For example, the ViewModel can be scoped to the NavBackStackEntry for the ListFragment.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }
        }

        KotlinCode(
            code = """
                    class ListFragment: Fragment() {
                        // Using the navGraphViewModels() Kotlin property delegate from the fragment-ktx
                        // artifact to retrieve the ViewModel using the NavBackStackEntry scope.
                        // R.id.list_fragment == the destination id of the ListFragment destination
                        private val viewModel: ListViewModel by navGraphViewModels(R.id.list_fragment)

                        override fun onViewCreated(view: View, savedInstanceState: Bundle?) {
                            viewModel.filteredList.observe(viewLifecycleOwner, Observer { item ->
                                // Update the list UI.
                            }
                        }
                    }
                    """.trimIndent()
        )

        SpanText(
            text = "Communicate using Fragment Result API",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "In some cases, you might want to pass a one-time value between two fragments or between a fragment and its host activity. For example, you might have a fragment that reads user’s feedback and pass the data back to a previous fragment.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "For example, the ViewModel can be scoped to the NavBackStackEntry for the ListFragment.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "Pass results between fragments",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                        .fontWeight(FontWeight.Bold)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "To pass data back to fragment A from fragment B, first set a result listener on fragment A, the fragment that receives the result. Call setFragmentResultListener() on fragment A's FragmentManager, as shown in the following example:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    override fun onCreate(savedInstanceState: Bundle?) {
                        super.onCreate(savedInstanceState)
                        setFragmentResultListener("requestKey") { requestKey, bundle ->
                            // We use a String here, but any type that can be put in a Bundle is supported.
                            val result = bundle.getString("bundleKey")
                            // Do something with the result.
                        }
                    }
                    """.trimIndent()
            )

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "In fragment B, the fragment producing the result, set the result on the same FragmentManager by using the same requestKey. You can do so by using the setFragmentResult() API:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    button.setOnClickListener {
                        val result = "result"
                        // Use the Kotlin extension in the fragment-ktx artifact.
                        setFragmentResult("requestKey", bundleOf("bundleKey" to result))
                    }
                    """.trimIndent()
            )

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "Fragment A then receives the result and executes the listener callback once the fragment is STARTED.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }
        }

        SpanText(
            text = "Pass results between parent and child fragments",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "To pass a result from a child fragment to a parent, use getChildFragmentManager() from the parent fragment instead of getParentFragmentManager() when calling setFragmentResultListener().",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    override fun onCreate(savedInstanceState: Bundle?) {
                        super.onCreate(savedInstanceState)
                        // Set the listener on the child fragmentManager.
                        childFragmentManager.setFragmentResultListener("requestKey") { key, bundle ->
                            val result = bundle.getString("bundleKey")
                            // Do something with the result.
                        }
                    }
                    """.trimIndent()
            )

            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "The child fragment sets the result on its FragmentManager. The parent then receives the result once the fragment is STARTED:",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    button.setOnClickListener {
                        val result = "result"
                        setFragmentResult("requestKey", bundleOf("bundleKey" to result))
                    }
                    """.trimIndent()
            )
        }

        SpanText(
            text = "Receive results in the host activity",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "To receive a fragment result in the host activity, set a result listener on the fragment manager using getSupportFragmentManager().",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    class MainActivity : AppCompatActivity() {
                        override fun onCreate(savedInstanceState: Bundle?) {
                            super.onCreate(savedInstanceState)
                            supportFragmentManager
                                    .setFragmentResultListener("requestKey", this) { requestKey, bundle ->
                                // We use a String here, but any type that can be put in a Bundle is supported.
                                val result = bundle.getString("bundleKey")
                                // Do something with the result.
                            }
                        }
                    }
                    """.trimIndent()
            )
        }

        SpanText(
            text = "Communicate using Interface",
            modifier = Modifier.fontSize(FontSize.Large).padding(top = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "To facilitate communication between fragments, the approach is to utilise the Activity in which the fragments reside. As mentioned in the below example Activity needs to be wrapped with an interface containing the `sendData` method. This method will be invoked from the first fragment to transmit the required data to the second fragment.",
                    modifier = Modifier
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    // Your FirstFragment
                    class FirstFragment : Fragment() {
                      // Create the interface
                      interface SendMessage {
                          fun sendData(message: String)
                      }
                      ....

                      // Invoke the sendData method from the first fragment.
                      firstFragmentButton.setOnClickListener {
                          mainActivity.sendData(firstFragmentEditText.text.toString())
                      }
                    }

                    // Your activity extend the relevant interface.
                    class YourActivity : AppCompatActivity(), FirstFragment.SendMessage {

                    // Implement the `sendData` method in the Activity.
                      override fun sendData(message: String) {
                    // Ensure the `supportFragmentManager` is employed to locate the second fragment; otherwise, there's a high likelihood of encountering unexpected errors.
                        val secondFragment: SecondFragment = supportFragmentManager.findFragmentById(R.id.secondFragment) as SecondFragment
                        secondFragment.setSecondFragmentText(message);
                      }
                    }

                    // Your SecondFragment
                    class SecondFragment : Fragment() {


                      // function to capture the sent data and utilise it as needed.
                      fun setSecondFragmentText(message: String) {
                        secondFragmentText.text = message;
                      }
                    }
                    """.trimIndent()
            )
        }
    }
}